/**
 * 公共颜色工具。
 *
 * 之前 software_theme.ts 与 keyboard_appearance.ts 各自重复实现了一份
 * clamp / hex 解析 / 混色 / rgba 等基础函数，逻辑略有差异且容易失同步。
 * 这里收口为唯一实现，供两个主题模块复用，并允许后续视觉模块直接引用。
 */

export type RgbColor = {
  r: number
  g: number
  b: number
}

const HEX_LONG_RE = /^#[0-9a-fA-F]{6}$/
const HEX_SHORT_RE = /^#[0-9a-fA-F]{3}$/

export const clamp = (value: number, min: number, max: number): number => {
  return Math.min(max, Math.max(min, value))
}

/**
 * 将任意输入归一化为 6 位小写 hex；不合法时返回 fallback。
 * 同时兼容 3 位短 hex（#abc → #aabbcc）。
 */
export const normalizeHexColor = (value: unknown, fallback: string): string => {
  if (typeof value !== 'string') {
    return fallback
  }

  const trimmed = value.trim()
  if (HEX_LONG_RE.test(trimmed)) {
    return trimmed.toLowerCase()
  }

  if (HEX_SHORT_RE.test(trimmed)) {
    const [, r, g, b] = trimmed
    return `#${r}${r}${g}${g}${b}${b}`.toLowerCase()
  }

  return fallback
}

export const hexToRgb = (value: string): RgbColor => {
  const normalized = normalizeHexColor(value, '#000000')
  return {
    r: parseInt(normalized.slice(1, 3), 16),
    g: parseInt(normalized.slice(3, 5), 16),
    b: parseInt(normalized.slice(5, 7), 16),
  }
}

export const rgbToHex = (color: RgbColor): string => {
  const toChannel = (value: number) =>
    Math.round(clamp(value, 0, 255))
      .toString(16)
      .padStart(2, '0')
  return `#${toChannel(color.r)}${toChannel(color.g)}${toChannel(color.b)}`
}

/** 用于 CSS `rgb(r, g, b)` 形式的逗号字符串，例如 `255, 128, 0`。 */
export const rgbToString = (color: RgbColor): string => {
  return `${color.r}, ${color.g}, ${color.b}`
}

/** 在两个 hex 颜色之间按比例混合，ratio ∈ [0, 1]。 */
export const mixHexColor = (from: string, to: string, ratio: number): string => {
  const start = hexToRgb(from)
  const end = hexToRgb(to)
  const weight = clamp(ratio, 0, 1)

  return rgbToHex({
    r: Math.round(start.r + (end.r - start.r) * weight),
    g: Math.round(start.g + (end.g - start.g) * weight),
    b: Math.round(start.b + (end.b - start.b) * weight),
  })
}

/** 将 hex 颜色转为带透明度的 rgba 字符串。 */
export const withAlpha = (value: string, alpha: number): string => {
  const color = hexToRgb(value)
  return `rgba(${color.r}, ${color.g}, ${color.b}, ${clamp(alpha, 0, 1)})`
}

/** 与 withAlpha 同义，保留以兼容旧调用风格。 */
export const toRgba = withAlpha

/** 感知亮度（0-255）；常用于决定文字应当用浅色还是深色。 */
export const perceivedBrightness = (color: RgbColor): number => {
  return (color.r * 299 + color.g * 587 + color.b * 114) / 1000
}
